import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, LoginCredentials } from '../types'
import authService from '../services/auth'
import pushService from '../services/push'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
  updateUser: (userData: { nome: string; email: string }) => Promise<void>
  hasPermission: (roles: Array<'ADMIN' | 'TECNICO' | 'VENDEDOR'>) => boolean
  isAdmin: () => boolean
  isTecnico: () => boolean
  isVendedor: () => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    
    const initAuth = async () => {
      console.log('[AuthContext] Iniciando inicialização...')
      try {
        if (authService.isAuthenticated()) {
          const storedUser = authService.getStoredUser()
          if (storedUser && isMounted) {
            console.log('✅ Sessão restaurada:', storedUser.nome, '| Tipo:', storedUser.tipo)
            setUser(storedUser)
          }
        } else {
          console.log('[AuthContext] Sem sessão válida no localStorage')
        }
      } catch (error) {
        console.error('[AuthContext] Erro ao inicializar autenticação:', error)
      } finally {
        if (isMounted) {
          setIsLoading(false)
          console.log('✅ Auth inicializado - isLoading agora é FALSE')
        }
      }
    }

    initAuth()
    
    return () => {
      isMounted = false
    }
  }, [])

  // useEffect separado para configurar push APENAS quando sessão for restaurada
  useEffect(() => {
    if (!user || user.tipo !== 'ADMIN' || isLoading) {
      return
    }

    console.log('[Push] Iniciando configuração de push notifications...')
    
    const setupPush = async () => {
      // Aguardar para garantir que token está disponível
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      try {
        console.log('📱 Configurando push notifications para admin...')
        const hasPermission = await pushService.requestPermission()
        console.log('[Push] hasPermission:' , hasPermission)
        console.log('[Push] Verificando hasPermission...')
        if (hasPermission) {
          console.log('[Push] Chamando pushService.subscribe()...')
          await pushService.subscribe()
          console.log('✅ Push notifications ativadas!')
        } else {
          console.warn('[Push] Permissão de notificações negada')
        }
      } catch (error) {
        console.error('❌ Erro ao configurar push:', error)
      }
    }

    setupPush()
  }, [user, isLoading])

  const login = async (credentials: LoginCredentials) => {
    console.log('🔐 Iniciando login...')
    setIsLoading(true)
    
    try {
      const response = await authService.login(credentials)
      console.log('✅ Login bem-sucedido:', response.user.nome, '| Tipo:', response.user.tipo)
      
      setUser(response.user)
      
      // Aguardar para garantir que o estado é atualizado
      await new Promise(resolve => setTimeout(resolve, 300))
      
    } catch (error) {
      console.error('❌ Erro no login:', error)
      throw error
    } finally {
      setIsLoading(false)
      console.log('✅ Login finalizado - isLoading agora é FALSE')
    }
  }

  const logout = async () => {
    console.log('🔐 Logout...')
    setIsLoading(true)
    try {
      await authService.logout()
      setUser(null)
      console.log('✅ Logout realizado')
    } catch (error) {
      console.error('Erro ao fazer logout:', error)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const updateUser = async (userData: { nome: string; email: string }) => {
    const updatedUser = await authService.updateProfile(userData)
    setUser(updatedUser)
  }

  const hasPermission = (roles: Array<'ADMIN' | 'TECNICO' | 'VENDEDOR'>): boolean => {
    return authService.hasPermission(roles)
  }

  const isAdmin = (): boolean => {
    return authService.isAdmin()
  }

  const isTecnico = (): boolean => {
    return authService.isTecnico()
  }

  const isVendedor = (): boolean => {
    return authService.isVendedor()
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
    hasPermission,
    isAdmin,
    isTecnico,
    isVendedor
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const useRequireAuth = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    console.log('[useRequireAuth] isLoading:', isLoading, '| isAuthenticated:', isAuthenticated)
    
    // CRÍTICO: NÃO redirecionar enquanto ainda está carregando
    if (isLoading) {
      console.log('[useRequireAuth] Ainda carregando, aguardando...')
      return
    }

    // Verificar token no localStorage como fallback
    const token = localStorage.getItem('token')
    const hasToken = !!token

    if (!isAuthenticated && !hasToken) {
      console.warn('[useRequireAuth] ⚠️ Não autenticado e sem token - redirecionando para /login')
      navigate('/login', { replace: true })
    } else {
      console.log('[useRequireAuth] ✅ Autenticado, acesso permitido')
    }
  }, [isAuthenticated, isLoading, navigate])
}
